/* ============================================================
   pbk-reviews.js — カスタマーレビューページのページ送り挙動を補正する（Issue #287）

   対象 = Yotpo legacy testimonials widget（#yotpo-testimonials-custom-tab 配下）。
   widget 本体は書き換えられないため、外側から次の3点だけを面倒見る。

   本番 https://pocket-by-kishun.com/pages/reviews の実測で判明していた症状:
   ① ページャーを押すと widget が `document.documentElement.scrollTop` を
      約21ms間隔で書き換える自前アニメで **約1.4〜2.5秒** かけてスクロールする。
      その最中に一覧 DOM が差し替わり（クリックから約600ms後）ページ全体の高さが
      ±150px 変わるため、アニメの目標値がズレて画面がゆっくり流れ続ける
      ＝「遷移がスムーズにできない」。
   ② 着地点は「1件目のレビュー上端が画面最上部（y=0）」= タブ行・総合評価行が
      画面外へ抜けた位置。現在どのタブ／何件目を見ているのか分からなくなる
      ＝「遷移できてもバグが起きる」ように見える。
   ③ クリック〜差し替えまでの約600msは無反応（旧ページの内容が出たまま）。
      連打すると通信が二重に走る。

   対策:
   - ページャークリックを検知したら widget の自前スクロールアニメ（html の
     scrollTop 書き込み）を短時間だけ無効化し、こちら側で一覧先頭へ1回で移動する。
     ユーザーが自分でスクロールしたら即座に手を離す。
   - 一覧が差し替わったら着地位置を再補正（差し替えで高さが変わるため）。
   - 切替中は `.is-pbk-paging` を付けて手応えを出し、二重送信を防ぐ。

   防御方針: 依存している widget 内部 class（.yotpo-pager / .yotpo-modal-body 等）が
   変わった場合は「何も起きない」だけで済むよう、全て存在チェック付き・
   try/catch 付きで書く。scrollTop の差し替えは必ず元に戻す。
   ============================================================ */
(function () {
  'use strict';

  var ROOT_SEL = '#yotpo-testimonials-custom-tab';
  var GAP = 24; /* 一覧上端の上に残す余白 */
  var SUPPRESS_MS = 2600; /* widget アニメを黙らせる上限（実測アニメ長 約1.4〜2.5s） */
  var PAGING_MS = 5000; /* 切替中表示の安全タイムアウト */

  var root = document.querySelector(ROOT_SEL);
  if (!root) return;

  var suppressTimer = null;
  var pagingTimer = null;
  var pagingUntilMutation = false;

  /* ---------- 目標スクロール位置 ---------- */

  function pick() {
    for (var i = 0; i < arguments.length; i++) {
      var el = null;
      try {
        el = root.querySelector(arguments[i]);
      } catch (e) {
        el = null;
      }
      if (el) return el;
    }
    return null;
  }

  /* sticky/fixed ヘッダーがある場合だけその高さを避ける（本テーマは既定で
     sticky OFF = 0。設定変更で sticky になっても追従する） */
  function headerOffset() {
    var h = 0;
    var els = document.querySelectorAll('sticky-header, .header-wrapper, .section-header');
    for (var i = 0; i < els.length; i++) {
      var cs = window.getComputedStyle(els[i]);
      if (cs.position === 'sticky' || cs.position === 'fixed') {
        h = Math.max(h, els[i].getBoundingClientRect().height);
      }
    }
    return Math.round(h);
  }

  /* 一覧の「頭」= タブ行を画面内に残せる位置。無ければ順に代替へ落とす */
  function targetY() {
    var anchor = pick('.yotpo-nav-primary', '.yotpo-modal-bottom-line', '.yotpo-modal-body', '.yotpo-display-wrapper');
    if (!anchor) return null;
    var top = anchor.getBoundingClientRect().top + window.pageYOffset;
    var y = Math.round(top - headerOffset() - GAP);
    var max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    if (y < 0) y = 0;
    if (y > max) y = max;
    return y;
  }

  function scrollToY(y) {
    if (y === null) return;
    try {
      window.scrollTo({ top: y, left: 0, behavior: 'auto' });
    } catch (e) {
      window.scrollTo(0, y);
    }
  }

  /* ---------- widget の自前スクロールアニメを黙らせる ---------- */
  /* widget は html(と環境により body)の scrollTop に連続代入してアニメする。
     その setter だけを一時的に空にする。window.scrollTo は別経路なので
     こちら側の移動は影響を受けない。 */

  var patched = [];

  function suppressWidgetScroll() {
    if (patched.length) return;
    var desc;
    try {
      desc = Object.getOwnPropertyDescriptor(Element.prototype, 'scrollTop');
    } catch (e) {
      desc = null;
    }
    if (!desc || typeof desc.get !== 'function' || typeof desc.set !== 'function') return;

    var targets = [document.documentElement, document.body];
    for (var i = 0; i < targets.length; i++) {
      var el = targets[i];
      if (!el) continue;
      try {
        (function (node) {
          Object.defineProperty(node, 'scrollTop', {
            configurable: true,
            enumerable: false,
            get: function () {
              return desc.get.call(node);
            },
            set: function () {
              /* Issue #287: widget のスクロールアニメを無視する */
            }
          });
        })(el);
        patched.push(el);
      } catch (e) {
        /* 差し替え不可な環境では従来挙動のまま */
      }
    }
  }

  function releaseWidgetScroll() {
    while (patched.length) {
      var el = patched.pop();
      try {
        delete el.scrollTop;
      } catch (e) {
        /* noop */
      }
    }
    if (suppressTimer) {
      clearTimeout(suppressTimer);
      suppressTimer = null;
    }
    detachUserIntent();
  }

  /* ---------- ユーザーが自分でスクロールしたら以降の補正をやめる ---------- */
  /* ここで scrollTop の差し替えまで戻すと、まだ動いている widget のアニメが
     再開してユーザーの操作を引き戻してしまう。差し替えはタイマー満了まで保ち、
     「こちらからの位置補正」だけを止める。 */

  var INTENT = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
  var intentBound = false;
  var userTookOver = false;

  function onUserIntent() {
    userTookOver = true;
  }

  function attachUserIntent() {
    if (intentBound) return;
    for (var i = 0; i < INTENT.length; i++) {
      window.addEventListener(INTENT[i], onUserIntent, { passive: true });
    }
    intentBound = true;
  }

  function detachUserIntent() {
    if (!intentBound) return;
    for (var i = 0; i < INTENT.length; i++) {
      window.removeEventListener(INTENT[i], onUserIntent);
    }
    intentBound = false;
  }

  /* ---------- 切替中の表示状態 ---------- */

  function pagingHost() {
    return pick('.yotpo.yotpo-modal', '.yotpo', '.yotpo-display-wrapper');
  }

  function startPaging() {
    var host = pagingHost();
    if (host) {
      host.classList.add('is-pbk-paging');
      host.setAttribute('aria-busy', 'true');
    }
    pagingUntilMutation = true;
    if (pagingTimer) clearTimeout(pagingTimer);
    pagingTimer = setTimeout(endPaging, PAGING_MS);
  }

  function endPaging() {
    pagingUntilMutation = false;
    if (pagingTimer) {
      clearTimeout(pagingTimer);
      pagingTimer = null;
    }
    var host = pagingHost();
    if (host) {
      host.classList.remove('is-pbk-paging');
      host.removeAttribute('aria-busy');
    }
  }

  /* ---------- クリック（委譲なので widget が再描画しても貼り直し不要） ---------- */

  root.addEventListener(
    'click',
    function (ev) {
      var a = ev.target && ev.target.closest ? ev.target.closest('.yotpo-pager .yotpo-page-element') : null;
      if (!a) return;
      if (a.classList.contains('yotpo-disabled') || a.classList.contains('yotpo-active')) return;

      var y = targetY();

      userTookOver = false;
      sigAtClick = listSignature();
      sigLast = sigAtClick;
      sigChanged = false;
      startPaging();
      suppressWidgetScroll();
      attachUserIntent();
      if (suppressTimer) clearTimeout(suppressTimer);
      suppressTimer = setTimeout(releaseWidgetScroll, SUPPRESS_MS);

      /* 一覧より下にいる時だけ引き上げる（勝手に下へ飛ばさない） */
      if (y !== null && window.pageYOffset > y + 2) scrollToY(y);
    },
    false
  );

  /* ---------- 一覧の差し替えを見張って着地位置を再補正 ---------- */

  /* ページャーの class 付け替えだけでも MutationObserver は鳴るため、
     「本当に一覧が入れ替わったか」はレビュー ID の並びで判定する
     （class 変化で早合点すると、まだ高さが変わる前に補正して着地がズレる）。 */
  function listSignature() {
    var ids = [];
    var list;
    try {
      list = root.querySelectorAll('.yotpo-review');
    } catch (e) {
      return '';
    }
    for (var i = 0; i < list.length; i++) {
      ids.push(list[i].getAttribute('data-review-id') || list[i].id || i);
    }
    return ids.join(',');
  }

  var sigAtClick = '';
  var sigLast = '';
  var sigChanged = false;
  var settleTimer = null;
  var recorrectTimer = null;

  function correctY() {
    if (userTookOver) return;
    var y = targetY();
    if (y !== null && window.pageYOffset > y + 2) scrollToY(y);
  }

  /* 差し替えで一覧の高さが変わり目標位置がズレるので、落ち着いてからもう一度合わせる。
     scrollTop の差し替え解除はクリック時のタイマーに任せる（ここで解くと
     まだ走っている widget のアニメが復活して画面が流れ続ける）。 */
  function onListSettled() {
    settleTimer = null;
    if (!pagingUntilMutation) return;
    if (sigChanged) correctY();
    endPaging();
    /* read-more の折り畳み等で直後にもう一段高さが動くので保険でもう1回 */
    if (recorrectTimer) clearTimeout(recorrectTimer);
    recorrectTimer = setTimeout(correctY, 300);
  }

  if (typeof MutationObserver === 'function') {
    var mo = new MutationObserver(function () {
      localizePager();
      if (!pagingUntilMutation) return;
      /* widget はクリック直後に一覧を空にしてから新しい行を差し込むため、
         「1回変わった」では早すぎる。変化が 220ms 止まったら確定とみなす。 */
      var sig = listSignature();
      if (sig === sigLast) return;
      sigLast = sig;
      if (sig && sig !== sigAtClick) sigChanged = true;
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(onListSettled, 220);
    });
    try {
      mo.observe(root, { childList: true, subtree: true });
    } catch (e) {
      /* noop */
    }
  }

  /* ---------- 前後ボタンのラベルを日本語に（読み上げ・title 表示用） ---------- */

  function localizePager() {
    var map = [
      ['.yotpo-pager .yotpo_previous', '前のページ'],
      ['.yotpo-pager .yotpo_next', '次のページ']
    ];
    for (var i = 0; i < map.length; i++) {
      var el = null;
      try {
        el = root.querySelector(map[i][0]);
      } catch (e) {
        el = null;
      }
      if (!el) continue;
      if (el.getAttribute('aria-label') !== map[i][1]) el.setAttribute('aria-label', map[i][1]);
      if (el.getAttribute('title') !== map[i][1]) el.setAttribute('title', map[i][1]);
    }
  }

  localizePager();

  /* ページ離脱時に差し替えを残さない */
  window.addEventListener('pagehide', releaseWidgetScroll);
})();
